import React, { useEffect, useRef, useState } from "react";
import RateDisclaimer from "./RateDisclaimer";

/**
 * Three mortgage-quote.me calculators in a tabbed UI.
 * Each calculator's vendor script auto-injects its widget alongside the
 * <script> tag. We mount all three host divs at once but only show the
 * active tab — the scripts continue to live in their hidden hosts so we
 * don't lose state when switching tabs.
 */
const COMPANY_ID = "69ea34dcffff3f4c2c9b5d36";

const TABS = [
    {
        key: "repayment",
        label: "Repayment",
        scriptId: "mqcapitalcalculator",
        src: `https://frontend.mortgage-quote.me/calculators/capital/mq-capital-calc.js?id=${COMPANY_ID}`,
        blurb:
            "See the monthly repayment for any property value, deposit and term — instantly.",
    },
    {
        key: "borrow",
        label: "How much can I borrow?",
        scriptId: "mqborrowcalculator",
        src: `https://frontend.mortgage-quote.me/calculators/borrow/mq-borrow-calc.js?id=${COMPANY_ID}`,
        blurb:
            "Estimate the maximum mortgage amount UK lenders may offer based on your income.",
    },
    {
        key: "overpayment",
        label: "Overpayment",
        scriptId: "mqoverpaymentcalculator",
        src: `https://frontend.mortgage-quote.me/calculators/overpayment/mq-overpayment-calc.js?id=${COMPANY_ID}`,
        blurb:
            "Find out how much time and interest you save by overpaying your mortgage.",
    },
    {
        key: "stamp-duty",
        label: "Stamp Duty",
        scriptId: "mqstampdutycalculator",
        src: `https://frontend.mortgage-quote.me/calculators/stampduty/mq-stamp-duty-calc.js?id=${COMPANY_ID}`,
        blurb:
            "UK stamp duty figures for England & Northern Ireland — first-time, mover or BTL.",
    },
    {
        key: "btl-max-mortgage",
        label: "BTL · Max Mortgage",
        scriptId: "mqbtlmaxmortgagecalculator",
        src: `https://frontend.mortgage-quote.me/calculators/btl-max-mortgage/mq-btl-max-mortgage-calc.js?id=${COMPANY_ID}`,
        blurb:
            "Buy-to-let: see the maximum mortgage your projected rent could support.",
    },
    {
        key: "btl-max-rent",
        label: "BTL · Max Rent",
        scriptId: "mqbtlmaxrentcalculator",
        src: `https://frontend.mortgage-quote.me/calculators/btl-max-rent/mq-btl-max-rent-calc.js?id=${COMPANY_ID}`,
        blurb:
            "Buy-to-let: work backwards from a target loan to find the rent it requires.",
    },
];

function CalculatorHost({ tab, active }) {
    const hostRef = useRef(null);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        if (document.getElementById(tab.scriptId)) return;

        const s = document.createElement("script");
        s.async = true;
        s.id = tab.scriptId;
        s.src = tab.src;
        host.appendChild(s);
    }, [tab]);

    return (
        <div
            ref={hostRef}
            data-testid={`calc-host-${tab.key}`}
            style={{ display: active ? "block" : "none" }}
            className="min-h-[420px]"
        />
    );
}

export default function MortgageCalculatorsSuite() {
    const [active, setActive] = useState(TABS[0].key);

    return (
        <section
            id="mortgage-calculators-section" data-testid="mortgage-calculators-section"
            className="relative py-14 md:py-20 bg-brand-bg"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Run the numbers
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                            Six calculators,
                            <br />
                            <em className="italic font-light text-brand-accent">
                                one quick answer.
                            </em>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8 flex md:items-end">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            Estimate your monthly repayment, see how much you
                            could borrow, model overpayments, work out stamp
                            duty, or run the buy-to-let numbers — switch
                            between them with a tap.
                        </p>
                    </div>
                </div>

                <div>
                    <div className="bg-brand-surface border border-brand-border rounded-[18px] shadow-[0_30px_60px_-25px_rgba(30,79,138,0.22)] overflow-hidden">
                        {/* Tabs */}
                        <div
                            role="tablist"
                            className="flex flex-wrap border-b border-brand-border bg-brand-surfaceAlt/40"
                        >
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    role="tab"
                                    aria-selected={active === t.key}
                                    data-testid={`calc-tab-${t.key}`}
                                    onClick={() => setActive(t.key)}
                                    className={`flex-1 min-w-[150px] px-5 py-4 text-sm font-bold uppercase tracking-[0.12em] transition-colors ${
                                        active === t.key
                                            ? "bg-brand-surface text-brand-primary border-b-2 border-brand-accent"
                                            : "text-brand-muted hover:text-brand-primary"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="p-6 md:p-10">
                            <RateDisclaimer className="mb-6" />
                            <p className="text-sm text-brand-muted mb-3">
                                {TABS.find((t) => t.key === active)?.blurb}
                            </p>
                            <p
                                data-testid="calc-placeholder-hint"
                                className="mb-6 text-xs text-brand-muted/80 italic leading-relaxed"
                            >
                                Greyed example numbers inside the input boxes are
                                placeholders — please type your own figures before
                                pressing Calculate.
                            </p>
                            {(active === "btl-max-mortgage" || active === "btl-max-rent") && (
                                <div
                                    data-testid="btl-risk-warning"
                                    className="mb-6 bg-brand-primary/10 border-l-4 border-brand-accent px-5 py-4 rounded space-y-2"
                                >
                                    <p className="text-sm md:text-base font-bold text-brand-primary leading-snug">
                                        Most Buy-to-Let mortgages are not regulated by the
                                        Financial Conduct Authority.
                                    </p>
                                    <p className="text-xs md:text-sm text-brand-muted leading-relaxed">
                                        Your property may be repossessed if you do not keep
                                        up repayments on a mortgage secured against it.
                                        Lending and rental projections shown by these
                                        calculators are estimates only and not a formal
                                        offer of credit.
                                    </p>
                                </div>
                            )}
                            {TABS.map((t) => (
                                <CalculatorHost
                                    key={t.key}
                                    tab={t}
                                    active={active === t.key}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
